import { HTMLAttributes, useMemo } from 'react';
import styled from '@emotion/styled';
import { Badge, Tooltip, Checkbox, Typography } from 'antd';
import { StarFilled } from '@ant-design/icons';
import { LazyAvatar } from 'components/LazyAvatar';
import dayjs from 'lib/dayjs';
import { useCurrentConversationIdValue } from 'hooks/business/conversation';
import { useTransAppProductName } from 'hooks/useTrans';
import { starColors, hasStar } from 'views/Conversation/ConvList/starData';
import {
  useBatchForwardEnable,
  useSetBatchForwardConvIds,
  useBatchForwardConvIds,
  useConvCanNotForward,
} from 'hooks/business/conversationsHooks/useConversationBatchForward';
import { useMemoizedFn } from 'ahooks';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import ConvOps from './ConvOps';
import shopeeReadSvg from './assets/shopeeRead.svg';
import lazadaReadSvg from './assets/lazadaRead.svg';
import chatttReadSvg from './assets/chatttRead.svg';
import bothReadSvg from './assets/bothRead.svg';
import remindSvg from './assets/remind.svg';
import blockSvg from './assets/blockMark.svg';
import useDemoCompatUnreadCount from './hooks/useDemoCompatUnreadCount';
import { useGetAvatarSrc } from 'lib/utils/platformImage';
import { QIFeature } from '../../../utils/featureFlag';
import { ConversationTag } from './ConversationTag';
import Countdown from '../components/Countdown';
import { LastMessage } from './LastMessage';
import { useTranslate } from '@tolgee/react';
import { useImportantTodoValue } from './FilterTodo';
import { useGetTodoTypeLabel } from 'views/Todo/hooks/useLabel';
import { isNil } from 'lodash-es';
import { ChannelIcon } from './ChannelIcon';
import { ConversationProfile } from './ConversationProfile';
import { ConversationCategoryType } from 'hooks/business/conversationsHooks/useConversationCategory';
import { isIdEqual } from 'utils/is';

const Extra = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  > * {
    margin-left: 8px;
  }
`;

const Read = styled.img`
  width: 15px;
  height: 15px;
  cursor: pointer;
`;

const CustomIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const StickTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  border: 5px solid #d6e8ff;
  border-right-color: transparent;
  border-bottom-color: transparent;
`;

const TodoType = styled.div`
  display: inline-block;
  max-width: calc(100% - 40px);
  margin: 4px 0 -4px 40px;
  padding: 0 6px;
  line-height: 22px;
  color: var(--ant-primary-color);
  background: var(--ant-primary-1);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
`;

const displayOfficialRead = false;

export interface ConversationProps extends HTMLAttributes<HTMLDivElement> {
  conversation: Conversation.Conversation;
  active: boolean;
  showCountdown?: boolean;
  onForward?: (conversation: Conversation.Conversation) => void;
  showOps?: boolean;
  category: ConversationCategoryType;
}

function Conversation(props: ConversationProps) {
  const {
    conversation,
    active,
    showCountdown = false,
    onForward,
    showOps,
    onClick,
    category,
  } = props;

  const {
    conversationId,
    channel,
    processed,
    lastMsgTime,
    receptionName,
    controlStatus,
    supervisePower,
    buyerAvatar,
    buyerNick,
    // 使用兼容demo版本的hooks获取该值
    // unreadCount,
    botMark,
    superviseMark,
    transferMark,
    colorAsterisk,
    officialRead,
    assistantRead,
    blacklistFlag,
    blacklistReason,
    blacklistRemark,
    remindMark = false,
    isAffiliateSip,
    region,
    mediation,
    reputation,
    fstUnprocessedMsgTimestamp,
    todoType,
    ttsToko,
    todoTypeName,
    existKnowledgePopup,
    pauseAiReply,
  } = conversation;
  const isMine = category === 'mine';
  const isAll = category === 'all';

  const { getTodoTypeLabel } = useGetTodoTypeLabel();

  const isRemind = remindMark && (controlStatus || supervisePower);

  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const { importantTodoTypes } = useImportantTodoValue() || {};

  const currentConversationId = useCurrentConversationIdValue();

  const batchForwardConvIds = useBatchForwardConvIds();
  const setBatchForwardConvIds = useSetBatchForwardConvIds();
  const batchForwardEnable = useBatchForwardEnable();
  const convCanNotForward = useConvCanNotForward();

  const isCurrentConversation = isIdEqual(conversationId, currentConversationId);

  const displayUnreadCount = isMine && !isCurrentConversation && controlStatus;

  const compatUnreadCount = useDemoCompatUnreadCount(conversationId);
  const getAvatarSrc = useGetAvatarSrc();
  const opsEnable = isNil(showOps) ? !QIFeature : showOps;

  const profile = (
    <ConversationProfile
      buyerNick={buyerNick}
      channel={channel}
      region={region}
      botMark={botMark}
      superviseMark={superviseMark}
      transferMark={transferMark}
      pauseAiReply={pauseAiReply}
      existKnowledgePopup={existKnowledgePopup}
    />
  );

  const time = (
    <span className="ml-2 text-[10px] text-[#b5b9bf]">
      {dayjs(parseInt(lastMsgTime, 10)).format('MM-DD HH:mm')}
    </span>
  );

  const read = useMemo(() => {
    if (!supervisePower) {
      return null;
    }

    if (displayOfficialRead && officialRead && assistantRead && receptionName) {
      return (
        <Tooltip title={appProductNameT('convList.bothRead')}>
          <Read src={bothReadSvg} alt="bothRead" />
        </Tooltip>
      );
    }

    if (assistantRead && receptionName) {
      return (
        <Tooltip title={t('convList.read')}>
          <Read src={chatttReadSvg} alt="chatttRead" />
        </Tooltip>
      );
    }

    if (displayOfficialRead && officialRead) {
      if (channel === 'LAZADA') {
        return (
          <Tooltip title={`Lazada ${t('convList.read')}`}>
            <Read src={lazadaReadSvg} alt="lazadaRead" />
          </Tooltip>
        );
      }

      if (channel === 'SHOPEE') {
        return (
          <Tooltip title={`Shopee ${t('convList.read')}`}>
            <Read src={shopeeReadSvg} alt="shopeeRead" />
          </Tooltip>
        );
      }
    }

    return null;
  }, [supervisePower, officialRead, assistantRead, channel, t, receptionName, appProductNameT]);

  const mark = useMemo(() => {
    if (isRemind && hasStar(colorAsterisk)) {
      return (
        <>
          <CustomIcon src={remindSvg} alt="remind" />
          <StarFilled style={{ color: starColors[colorAsterisk] }} />
        </>
      );
    }

    if (isRemind) {
      return <CustomIcon src={remindSvg} alt="remind" />;
    }

    if (blacklistFlag) {
      return (
        <Tooltip
          overlay={
            <>
              <div>{blacklistReason}</div>
              <div>{blacklistRemark}</div>
            </>
          }
          destroyTooltipOnHide
        >
          <CustomIcon src={blockSvg} alt="block" />
        </Tooltip>
      );
    }

    if (hasStar(colorAsterisk)) {
      return <StarFilled style={{ color: starColors[colorAsterisk] }} />;
    }

    return null;
  }, [isRemind, blacklistFlag, blacklistReason, blacklistRemark, colorAsterisk]);

  const showReceptionName = useMemo(() => {
    return Boolean((supervisePower || isAll) && !!receptionName);
  }, [supervisePower, receptionName, isAll]);

  const extra = (
    <Extra>
      {showReceptionName && (
        <Typography.Paragraph
          className="!max-w-[64px] !ml-1 !mb-0 !text-content-400 text-xs"
          ellipsis={{
            tooltip: receptionName,
          }}
        >
          {receptionName}
        </Typography.Paragraph>
      )}
      {read}
      {mark}
    </Extra>
  );

  const content = (
    <div className="flex flex-col justify-around overflow-hidden flex-grow flex-shrink-0 basis-0">
      <div className="flex items-center justify-between overflow-hidden">
        {profile}
        {time}
      </div>
      <div className="flex items-center justify-between overflow-hidden">
        <LastMessage
          className="flex-1 text-sm text-[#84868c] mr-[14px]"
          conversation={conversation}
        />
        {extra}
      </div>
    </div>
  );

  const backgroundColor = useMemo(() => {
    if (isAffiliateSip) {
      return active ? '#E7E9EE' : 'transparent';
    }

    if (isRemind) {
      return active ? '#FCD45C' : '#FFE492';
    }

    if (!processed) {
      return active ? '#FFE2C8' : '#FFF4EB';
    }

    return active ? '#ECECED' : 'transparent';
  }, [active, isAffiliateSip, isRemind, processed]);

  const handleConversationCheck = useMemoizedFn((e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    if (checked) {
      setBatchForwardConvIds(conversationId);
    } else {
      setBatchForwardConvIds(conversationId, 'del');
    }
  });

  const isChecked = useMemo(
    () => batchForwardConvIds?.includes(conversationId),
    [batchForwardConvIds, conversationId],
  );

  const contentNode = (
    <div
      className="relative p-1 cursor-pointer border-t-0 border-r-0 border-b border-l-0 border-solid border-[#F0F2F5]"
      data-key="convItem"
      data-ph-capture-attribute-convitem="convitem"
      onClick={onClick}
    >
      <div
        className="rounded-sm p-2"
        style={{
          backgroundColor,
        }}
      >
        <div className="flex items-start break-all gap-2">
          {batchForwardEnable ? (
            <Checkbox
              checked={isChecked}
              disabled={!controlStatus || convCanNotForward(conversationId)}
              onChange={handleConversationCheck}
              onClick={(e) => e.stopPropagation()}
            />
          ) : null}
          <span className="inline-block relative">
            <Badge size="small" count={displayUnreadCount ? compatUnreadCount : 0}>
              <LazyAvatar
                src={buyerAvatar ? getAvatarSrc(buyerAvatar, channel) : undefined}
                size={32}
                shape="square"
              />
            </Badge>
            <ChannelIcon className="absolute right-0 bottom-0 w-3 h-3" channel={channel} />
          </span>
          {content}
        </div>
        {isRemind && <StickTop />}
        {mediation || reputation ? (
          <div className="pl-[40px] mt-1 flex items-center gap-1 bg">
            {reputation && <ConversationTag className="bg-[#F56E6E]" text={t('影响声誉')} />}
            {mediation && <ConversationTag className="bg-primary" text={t('调解中')} />}
          </div>
        ) : null}
        {ttsToko ? (
          <div className="pl-[40px] mt-1 flex items-center gap-1">
            <ConversationTag className="bg-primary" text={t('来自Tokoepdia')} />
          </div>
        ) : null}
        {showCountdown && fstUnprocessedMsgTimestamp && fstUnprocessedMsgTimestamp !== '0' && (
          <Countdown time={fstUnprocessedMsgTimestamp} />
        )}
        {importantTodoTypes?.includes(todoType) && (
          <TodoType title={getTodoTypeLabel(todoType, todoTypeName)}>
            {getTodoTypeLabel(todoType, todoTypeName)}
          </TodoType>
        )}
      </div>
    </div>
  );

  if (opsEnable) {
    return (
      <ConvOps
        category={category}
        trigger={['contextMenu']}
        talk={conversation}
        onForward={onForward}
      >
        {contentNode}
      </ConvOps>
    );
  }

  return contentNode;
}

export default Conversation;
